import { useRef, useState } from "react";
import axios from "axios";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { resolveImg } from "@/admin/SiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Upload widget: drag/drop or click to upload, shows preview, returns URL via onChange. */
export default function ImageUpload({ value, onChange, label = "Immagine", height = 220 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un'immagine (JPG, PNG, WEBP, GIF)");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("File troppo grande (max 12 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Immagine caricata");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e.message || "Upload fallito",
      );
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  return (
    <div className="mb-5">
      <span className="block text-[10px] uppercase tracking-[0.26em] text-[#D4AF37] mb-2">
        {label}
      </span>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="relative rounded-2xl overflow-hidden flex items-center justify-center bg-[#0B0B0F]/70 border border-dashed border-white/15 hover:border-[#D4AF37]/45 transition-colors"
        style={{ minHeight: height }}
        data-testid="image-upload-zone"
      >
        {value ? (
          <>
            <img
              src={resolveImg(value)}
              alt=""
              className="max-h-[260px] w-auto object-contain"
            />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#0B0B0F]/90 border border-white/15 text-[#E26B6B] hover:border-[#E26B6B]/55 inline-flex items-center justify-center"
              aria-label="Rimuovi immagine"
            >
              <X size={14} strokeWidth={2.4} />
            </button>
          </>
        ) : (
          <div className="text-center p-8 text-[#B6B6BE]">
            <ImageIcon size={28} className="mx-auto mb-3 text-[#D4AF37]/70" strokeWidth={1.6} />
            <p className="text-sm">Trascina qui un&apos;immagine</p>
            <p className="text-[11px] text-[#B6B6BE]/60 mt-1">
              JPG, PNG, WEBP, GIF — max 12 MB
            </p>
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={onPick}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="image-upload-button"
          className="btn-ghost-neon inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em] disabled:opacity-60"
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {uploading ? "Caricamento…" : value ? "Sostituisci immagine" : "Carica immagine"}
        </button>
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="oppure incolla un URL"
          className="flex-1 min-w-[200px] px-3 py-2 rounded-full bg-[#0B0B0F]/90 border border-white/10 text-xs text-[#B6B6BE] focus:outline-none focus:border-[#00D4FF]/45"
        />
      </div>
    </div>
  );
}
